import { privateConfig } from "../../config";
import {
	CONTENTFUL_ZENDESK_TYPES,
	getAuthHeader,
	defaultLocale,
	ZENDESK_TYPES,
	getRecordUrl,
	pagePaths,
	transformRecord,
	contentfulDefaultLocale,
	ZENDESK_TYPES_NAME,
	ZENDESK_SOURCES_NAME,
} from "../../domain/zendesk";
import { handleResponse } from "../../utils/handleResponse";

const zendeskService = ({ publicConfig, contentfulService }) => {
	const { ZENDESK_URL, ZENDESK_EMAIL, STATIC_SITE_DOMAIN } = publicConfig;

	const auth = getAuthHeader(ZENDESK_EMAIL, privateConfig.ZENDESK_TOKEN);

	const baseUrl = `${ZENDESK_URL}/api/v2/guide/external_content`;

	const headers = {
		"Content-Type": "application/json",
		Authorization: `Basic ${auth}`,
	};

	const fetchParams = { headers };

	const updateRecordById = (id, body) =>
		fetch(`${baseUrl}/records/${id}`, {
			method: "PUT",
			body: JSON.stringify(body),
			...fetchParams,
		})
			.then(handleResponse)
			.then(({ record }) => record);

	const createRecord = (newRecord) =>
		fetch(`${baseUrl}/records`, {
			method: "POST",
			body: JSON.stringify(newRecord),
			...fetchParams,
		})
			.then(handleResponse)
			.then(({ record }) => record);

	const getTypes = () =>
		fetch(`${baseUrl}/types`, fetchParams)
			.then(handleResponse)
			.then(({ types }) => types);

	const getSources = () =>
		fetch(`${baseUrl}/sources`, fetchParams)
			.then(handleResponse)
			.then(({ sources }) => sources);

	const getRecordById = async (id) =>
		fetch(`${baseUrl}/records/${id}`, fetchParams).then((res) => {
			if (!res.ok) {
				return { record: null };
			}

			return res.json();
		});

	const getTypeIdByTypeName = async (typeName) => {
		const types = await getTypes();

		const typeId = types.find((type) => type.name === typeName)?.id;

		return typeId;
	};

	const getSourceIdBySourceName = async (sourceName) => {
		const sources = await getSources();

		const sourceId = sources.find((source) => source.name === sourceName)?.id;

		return sourceId;
	};

	const getSourceAndTypeIdsByContentType = async (type) => {
		const zendeskTypeName = ZENDESK_TYPES_NAME[type];
		const zendeskSourceName = ZENDESK_SOURCES_NAME[type];

		const [typeId, sourceId] = await Promise.all([
			getTypeIdByTypeName(zendeskTypeName),
			getSourceIdBySourceName(zendeskSourceName),
		]);

		return { typeId, sourceId };
	};

	const prepareAndCreateRecord = async ({
		id,
		contentType,
		title,
		subtitle,
		lead,
		slug,
	}) => {
		const type = CONTENTFUL_ZENDESK_TYPES[contentType];

		const { sourceId, typeId } = await getSourceAndTypeIdsByContentType(type);

		if (!typeId || !sourceId) return null;

		const newRecord = {
			title,
			body: type === ZENDESK_TYPES.series ? subtitle : lead,
			external_id: id,
			locale: defaultLocale,
			source_id: sourceId,
			type_id: typeId,
			url: getRecordUrl({
				domain: STATIC_SITE_DOMAIN,
				path: pagePaths[contentType],
				slug,
			}),
		};

		const createdRecord = await createRecord({ record: newRecord });

		return createdRecord;
	};

	const createOrUpdateRecord = async (contentfulData) => {
		const { id, contentType, title, subtitle, lead, zendeskRecordId } =
			contentfulData;

		const type = CONTENTFUL_ZENDESK_TYPES[contentType];

		if (!type) return false;

		if (zendeskRecordId) {
			const { record } = await getRecordById(zendeskRecordId);

			if (record) {
				await updateRecordById(record.id, {
					record: {
						...transformRecord(record),
						title,
						body: type === "series" ? subtitle : lead,
					},
				});

				return true;
			}

			const createdRecord = await prepareAndCreateRecord(contentfulData);

			if (!createdRecord) return false;

			const updatedEntry = await contentfulService.updateZendeskRecordId(id, {
				[contentfulDefaultLocale]: createdRecord.id,
			});

			await contentfulService.publishEntryById(id, updatedEntry.sys.version);

			return true;
		}

		const createdRecord = await prepareAndCreateRecord(contentfulData);

		const updatedEntry = await contentfulService.updateZendeskRecordId(id, {
			[contentfulDefaultLocale]: createdRecord.id,
		});

		await contentfulService.publishEntryById(id, updatedEntry.sys.version);

		return true;
	};

	const removeRecordById = async (id) =>
		fetch(`${baseUrl}/records/${id}`, {
			method: "DELETE",
			...fetchParams,
		}).then((res) => res.status === 204 || res.status === 404);

	const removeRecord = async (contentfulData) => {
		const { id, contentType } = contentfulData;

		if (!id || !contentType) return false;

		const type = CONTENTFUL_ZENDESK_TYPES[contentType];

		if (!type) return false;

		const entry = await contentfulService.getEntryById(id);

		if (!entry) return false;

		const { zendeskRecordId } = entry.fields;

		if (!zendeskRecordId) return false;

		return removeRecordById(zendeskRecordId[contentfulDefaultLocale]);
	};

	return {
		createOrUpdateRecord,
		removeRecord,
	};
};

export default zendeskService;
